import React, { ChangeEvent, useId } from 'react';
import { Document } from '@contentful/rich-text-types';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';
import cx from 'classnames';

import RichText from '../../RichText';
import { EmbeddedInlineEntryNode } from '../../RichText/Nodes';
import GlLink from '../../Link';

import { formFieldEnum } from '../../SplitModule/Form/shared';

import whiteCheck from '@/images/white-checkbox-check.svg';

interface CheckboxProps {
  type: formFieldEnum.CHECKBOX | formFieldEnum.RADIO;
  value: string | number | readonly string[] | undefined;
  checked: boolean;
  label: string;
  name?: string;
  error?: string;
  required?: boolean;
  disclaimer?: Document | null;
  handleOnChange?: (
    event: ChangeEvent<HTMLInputElement>,
    value: boolean
  ) => void;
  testId?: string;
}

export function Checkbox({
  type,
  name,
  value,
  checked,
  required,
  label,
  disclaimer,
  handleOnChange
}: CheckboxProps) {
  const checkboxId = useId();

  return (
    <div className="flex flex-row items-start pb-6">
      <div className="px-2 py-[2px]">
        {type === formFieldEnum.RADIO ? (
          <input
            type="radio"
            id={checkboxId}
            name={name}
            value={value}
            checked={checked}
            required={required}
            onChange={(e) => {
              if (handleOnChange) {
                handleOnChange(e, e.target.checked);
              }
            }}
            data-testid={`radio-input-${label}`}
            className={cx(
              'radio-input',
              'font-inherit m-0 h-6 w-6 cursor-pointer rounded-full bg-white text-[1.5em]',
              'grid -translate-y-[0.075em] place-content-center',
              'border-2 text-gray',
              checked ? 'border-teal' : 'border-gray'
            )}
          />
        ) : (
          <input
            type="checkbox"
            id={checkboxId}
            checked={checked}
            name={name}
            value={value}
            required={required}
            onChange={(e) => {
              if (handleOnChange) {
                handleOnChange(e, e.target.checked);
              }
            }}
            data-testid={`checkbox-input-${label}`}
            className={cx(
              'checkbox-input',
              'font-inherit m-0 h-6 w-6 cursor-pointer rounded text-[1.5em]',
              'relative flex -translate-y-[0.075em] items-center justify-center',
              'border-2 border-gray bg-white'
            )}
            style={
              {
                '--checkbox-check-url': `url(${whiteCheck.src})`
              } as React.CSSProperties
            }
          />
        )}
      </div>
      <div className="flex flex-col desktop:px-2">
        <label
          htmlFor={checkboxId}
          className="cursor-pointer text-base font-normal leading-6"
        >
          {label}
        </label>

        {disclaimer && (
          <RichText
            data={disclaimer}
            overrideOptions={{
              [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                <p className="px-0 py-2 text-left text-sm font-normal leading-[18px] text-black">
                  {children}
                </p>
              ),
              [INLINES.HYPERLINK]: (node, children) => (
                <GlLink
                  href={node.data.uri}
                  className="text-black underline transition-colors duration-200 ease-in-out hover:text-navy desktop:hover:text-navy"
                >
                  {children}
                </GlLink>
              ),
              [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                <EmbeddedInlineEntryNode
                  node={node}
                  colour="defaultText"
                  fontWeight="p"
                >
                  {children}
                </EmbeddedInlineEntryNode>
              )
            }}
          />
        )}
      </div>
    </div>
  );
}
